import { api } from '@/lib/api/client';
import { getAuthToken, getActiveBranchId } from '@/lib/api/session';
import type {
  Bill, BillItem, BillPayment, CreateOpdBillInput, CreditNote, IssueCreditNoteInput, RecordPaymentInput,
} from './types';

/** GET /api/billing — bare array unless ?page= (see HMS_V4_API_CONTRACT.md §1). Branch-scoped. */
export function fetchBills(): Promise<Bill[]> {
  return api.get('/billing');
}

export function createOpdBill(input: CreateOpdBillInput): Promise<Bill> {
  return api.post('/billing/opd/create', input);
}

export function fetchBillItems(billId: number): Promise<BillItem[]> {
  return api.get(`/billing/${billId}/items`);
}

export function fetchBillPayments(billId: number): Promise<BillPayment[]> {
  return api.get(`/billing/${billId}/payments`);
}

export function recordPayment(billId: number, input: RecordPaymentInput): Promise<{ payment: BillPayment; bill: Bill }> {
  return api.post(`/billing/${billId}/payments`, input);
}

export function fetchBillCreditNotes(billId: number): Promise<CreditNote[]> {
  return api.get(`/billing/${billId}/credit-notes`);
}

export function issueCreditNote(billId: number, input: IssueCreditNoteInput): Promise<CreditNote> {
  return api.post(`/billing/${billId}/credit-notes`, input);
}

export function settleCreditNote(creditNoteId: number, refundMode?: string): Promise<CreditNote> {
  return api.patch(`/billing/credit-notes/${creditNoteId}/settle`, refundMode ? { refund_mode: refundMode } : undefined);
}

/**
 * Fetch-then-blob rather than a plain <a href>, same reasoning as the legacy
 * openPatientDocument: a raw navigation sends Accept: text/html, which the
 * dev-server proxy does not forward to the backend, so the PDF request never
 * arrives — fetching it ourselves and opening the resulting blob sidesteps
 * that in both dev and production.
 *
 * Now also used by the pharmacy walk-in counter (features/pharmacy/outpatient),
 * which issues its own invoices through the same /api/billing/:id/pdf route —
 * imported across features rather than duplicated, since duplicating it would
 * mean two places to fix the proxy/Accept-header problem described above.
 *
 * Kept local to this feature rather than promoted to lib/, unlike
 * queueStatusUrl — the identical pattern exists for patient documents too,
 * but that screen has not been migrated yet. Extract a shared helper once a
 * second real TS consumer needs it, not before.
 */
export async function openBillPdf(billId: number): Promise<void> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    const branchId = getActiveBranchId();
    if (branchId !== null) headers['X-Branch-Id'] = String(branchId);
  }
  const res = await fetch(`/api/billing/${billId}/pdf`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}) as { error?: string });
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank');
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
}
